import { NfcCard, Visitor } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { useMemo } from 'react';
import { getCardsByVisitorId } from 'utils/NfcCard/nfcCard';
import {
  getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers,
  getVisitorStatuses,
  isVisitorWithoutGroup,
  Statuses,
  translateRole
} from 'utils/Visitor/visitor';
import { getVisitorOriginLabel } from 'v2/utils/visitorOrigin';
import { useSearch } from './useSearch';

const visitorSearchColumns = [
  'name',
  'email',
  'searchableRoles',
  'searchableGroupNames',
  'searchableOrigin',
  'searchableCards',
  'searchableStatus'
];

const getSearchableStatus = (visitor: Visitor, cards: NfcCard[], translation: Translation): string => {
  const statuses = getVisitorStatuses(visitor, cards);
  switch (statuses) {
    case Statuses.isConfigDeviceDisconnected:
    case Statuses.isAccessDeviceDisconnectedAndVisitorHasNoCards:
    case Statuses.isAccessDeviceDisconnectedAndVisitorHasCards:
      return translation.disconnected;
    case Statuses.isVisitorExpireIn24H:
      return translation.expires_in_24h;
    case Statuses.isVisitorExpireIn1Week:
      return translation.expires_this_week;
    default:
      return '';
  }
};

const getSearchableCardNames = (visitor: Visitor, cards: NfcCard[]): string[] =>
  getCardsByVisitorId(visitor.id, cards).map((card) => card.name);

const getSearchableGroupNames = (visitor: Visitor, translation: Translation): string[] => {
  const groupNames = visitor.visitorGroupMembership
    .map((membership) => membership.visitorGroup?.name)
    .filter((name): name is string => Boolean(name));

  return isVisitorWithoutGroup(visitor) ? [...groupNames, translation.no_groups_assigned] : groupNames;
};

const getSearchableRoles = (visitor: Visitor, translation: Translation): string[] =>
  getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers(visitor).map((role) => translateRole(role, translation));

export function useVisitorSearch({
  visitors,
  cards,
  search,
  translation
}: {
  visitors: Visitor[];
  cards: NfcCard[];
  search: string;
  translation: Translation;
}): Visitor[] {
  const searchableVisitors = useMemo(
    () =>
      visitors.map((visitor) => ({
        ...visitor,
        searchableRoles: getSearchableRoles(visitor, translation),
        searchableGroupNames: getSearchableGroupNames(visitor, translation),
        searchableOrigin: getVisitorOriginLabel(visitor, translation),
        searchableCards: getSearchableCardNames(visitor, cards),
        searchableStatus: getSearchableStatus(visitor, cards, translation)
      })),
    [visitors, cards, translation]
  );

  const searchedVisitors = useSearch({
    data: searchableVisitors,
    search,
    keys: visitorSearchColumns
  });

  return useMemo(
    () =>
      searchedVisitors.map(
        ({ searchableRoles, searchableGroupNames, searchableOrigin, searchableCards, searchableStatus, ...visitor }) =>
          visitor
      ),
    [searchedVisitors]
  );
}
